import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import Navbar from "@/components/Navbar";
import LemonBot from "@/components/LemonBot";
import LemonBotFloater from "@/components/LemonBotFloater";
import MiddleSchoolLesson1Flow from "@/components/MiddleSchoolLesson1Flow";
import SessionFlow from "@/components/SessionFlow";
import { triggerConfetti } from "@/utils/confetti";
import { playCompletionSound, playSubmitSound } from "@/utils/sounds";
import { CheckCircle2, ArrowLeft, ArrowRight, Save } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

async function logActivity(action, data = {}) {
  try {
    const token = localStorage.getItem("token") || localStorage.getItem("student_token");
    if (!token) return;
    await fetch(`${API}/admin/log-activity`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action, ...data }),
    });
  } catch (e) { /* silent */ }
}

function MiddleSchoolLessonView({ user, onLogout }) {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [module, setModule] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [progress, setProgress] = useState([]);
  const [responses, setResponses] = useState({});
  const [savedStatus, setSavedStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dynamicPrompts, setDynamicPrompts] = useState(null);
  const [sessionV2, setSessionV2] = useState(null);  // Full v2 schema for SessionFlow
  const [moduleSiblings, setModuleSiblings] = useState([]);  // sessions in next module for cross-module navigation
  const saveTimer = useRef(null);

  useEffect(() => {
    loadLesson();
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, i18n.language]);

  const loadLesson = async () => {
    setLoading(true);
    try {
      const lang = i18n.language || 'en';
      // Fetch the v2 schema first (also gives us module_id)
      const v2Res = await axios.get(`${API}/lessons/${lessonId}/v2?lang=${lang}`);
      const v2 = v2Res.data;
      if (!v2 || v2.learning_path !== "middle_school") {
        navigate("/middle-school");
        return;
      }
      setSessionV2(v2.session_v2);

      // Find the module
      const modulesRes = await axios.get(`${API}/modules?learning_path=middle_school&lang=${lang}`);
      const allModules = (modulesRes.data || []).sort((a, b) => a.order - b.order);
      const myModule = allModules.find((m) => m.id === v2.module_id);
      setModule(myModule || null);

      // Sessions for this module + all siblings across modules for cross-module nav
      const moduleLessonsRes = await axios.get(`${API}/modules/${v2.module_id}/lessons?lang=${lang}`);
      const moduleLessons = (moduleLessonsRes.data || []).sort((a, b) => a.order - b.order);
      setLessons(moduleLessons);
      const lesson = moduleLessons.find((l) => l.id === lessonId);
      setCurrentLesson(lesson || null);

      // Compute next module's first session for cross-module flow
      const myModIdx = allModules.findIndex((m) => m.id === v2.module_id);
      if (myModIdx >= 0 && myModIdx < allModules.length - 1) {
        const nextModule = allModules[myModIdx + 1];
        try {
          const nextR = await axios.get(`${API}/modules/${nextModule.id}/lessons?lang=${lang}`);
          setModuleSiblings((nextR.data || []).sort((a, b) => a.order - b.order));
        } catch (e) { setModuleSiblings([]); }
      } else {
        setModuleSiblings([]);
      }

      const [progressRes, savedRes] = await Promise.all([
        axios.get(`${API}/progress/my`),
        axios.get(`${API}/lesson-responses/${lessonId}`).catch(() => ({ data: { responses: {} } })),
      ]);
      setProgress(progressRes.data || []);
      setResponses(savedRes.data?.responses || {});
    } catch (err) {
      console.error("Error loading middle-school lesson:", err);
    } finally {
      setLoading(false);
    }
  };

  const isLessonComplete = (id) =>
    progress.some((p) => p.lesson_id === id && p.completed);

  const currentIndex = lessons.findIndex((l) => l.id === lessonId);
  const prevLesson = currentIndex > 0 ? lessons[currentIndex - 1] : null;
  // Within-module next, else first session of next module
  const nextLesson = (() => {
    if (currentIndex >= 0 && currentIndex < lessons.length - 1) return lessons[currentIndex + 1];
    if (moduleSiblings.length > 0) return moduleSiblings[0];
    return null;
  })();

  const handleFieldChange = (fieldId, value) => {
    const updated = { ...responses, [fieldId]: value };
    setResponses(updated);
    setSavedStatus("");

    // Debounced auto-save
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveResponses(updated, true);
    }, 1000);
  };

  const saveResponses = async (data, isAuto = false) => {
    try {
      setSaving(true);
      await axios.post(`${API}/lesson-responses/${lessonId}`, { responses: data });
      setSavedStatus(isAuto ? "Saved ✓" : "Saved!");
      setTimeout(() => setSavedStatus(""), 2000);
    } catch (e) {
      setSavedStatus("Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Lesson-1 specific: persist the nested flow state (debounced via the same timer)
  const handleLesson1Change = (nestedState) => {
    setResponses(nestedState);
    setSavedStatus("");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveResponses(nestedState, true);
    }, 1000);
  };

  // Lesson-1 specific: invoked from the builder stage when student clicks "Finish Lesson 1"
  const handleLesson1Submit = async (builder) => {
    setSubmitting(true);
    try {
      // Final save (immediate, no debounce)
      const finalState = { ...responses, builder };
      await saveResponses(finalState, false);

      // Build a rich capstone draft using all 3 builder fields
      if (module?.capstone_field) {
        const drafted = [
          builder.business_name && `Business: ${builder.business_name}`,
          builder.problem && `Problem: ${builder.problem}`,
          builder.solution && `Solution: ${builder.solution}`,
          builder.profile && `Style: ${builder.profile}`,
        ].filter(Boolean).join("\n");
        if (drafted) {
          try {
            await axios.post(`${API}/capstone/progress`, {
              field: module.capstone_field,
              value: drafted,
            });
          } catch (e) { /* ignore */ }
        }
      }

      if (!isLessonComplete(lessonId)) {
        await axios.post(`${API}/lessons/${lessonId}/complete`);
        playCompletionSound();
        triggerConfetti();
        await logActivity("middle_school_lesson_completed", {
          details: { lesson_id: lessonId, lesson_title: currentLesson?.title },
        });
      }
      playSubmitSound();

      const progressRes = await axios.get(`${API}/progress/my`);
      setProgress(progressRes.data || []);

      if (nextLesson) {
        setTimeout(() => navigate(`/middle-school/lessons/${nextLesson.id}`), 800);
      } else {
        setTimeout(() => navigate("/middle-school"), 800);
      }
    } catch (e) {
      console.error("Submit failed:", e);
    } finally {
      setSubmitting(false);
    }
  };

  // Generic session_v2 submit handler — writes the build-stage fields as a capstone draft for the module
  const handleSessionV2Submit = async (submission) => {
    setSubmitting(true);
    try {
      const finalState = { ...responses, ...submission };
      await saveResponses(finalState, false);

      // Capstone: serialize build object into a readable draft
      // Per-session override takes priority over the module's default capstone_field
      const sessionCapstoneField =
        sessionV2?.capstone_field || module?.capstone_field;
      if (sessionCapstoneField && submission.build && Object.keys(submission.build).length > 0) {
        const drafted = Object.entries(submission.build)
          .filter(([, v]) => (v || "").trim())
          .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`)
          .join("\n");
        if (drafted) {
          try {
            await axios.post(`${API}/capstone/progress`, {
              field: sessionCapstoneField,
              value: drafted,
            });
          } catch (e) { /* ignore */ }
        }
      }

      if (!isLessonComplete(lessonId)) {
        await axios.post(`${API}/lessons/${lessonId}/complete`);
        playCompletionSound();
        triggerConfetti();
        await logActivity("middle_school_session_completed", {
          details: { lesson_id: lessonId, lesson_title: currentLesson?.title },
        });
      }
      playSubmitSound();

      const progressRes = await axios.get(`${API}/progress/my`);
      setProgress(progressRes.data || []);

      if (nextLesson) {
        setTimeout(() => navigate(`/middle-school/lessons/${nextLesson.id}`), 800);
      } else {
        setTimeout(() => navigate("/middle-school"), 800);
      }
    } catch (e) {
      console.error("Submit failed:", e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Save final responses
      await saveResponses(responses, false);

      // If first lesson with school_problem, copy as capstone draft
      if (currentLesson?.order === 1 && responses.school_problem && module?.capstone_field) {
        try {
          await axios.post(`${API}/capstone/progress`, {
            field: module.capstone_field,
            value: `Problem: ${responses.school_problem}`,
          });
        } catch (e) { /* ignore */ }
      }

      // If lesson 3 (Your Business Idea), build a full capstone draft
      if (currentLesson?.order === 3 && module?.capstone_field) {
        const drafted = [
          responses.my_business_name && `Business: ${responses.my_business_name}`,
          responses.my_problem && `Problem: ${responses.my_problem}`,
          responses.my_solution && `Solution: ${responses.my_solution}`,
        ].filter(Boolean).join("\n");
        if (drafted) {
          try {
            await axios.post(`${API}/capstone/progress`, {
              field: module.capstone_field,
              value: drafted,
            });
          } catch (e) { /* ignore */ }
        }
      }

      // Mark lesson complete (only if not already)
      if (!isLessonComplete(lessonId)) {
        await axios.post(`${API}/lessons/${lessonId}/complete`);
        playCompletionSound();
        triggerConfetti();
        await logActivity("middle_school_lesson_completed", {
          details: { lesson_id: lessonId, lesson_title: currentLesson?.title },
        });
      }
      playSubmitSound();

      // Reload progress
      const progressRes = await axios.get(`${API}/progress/my`);
      setProgress(progressRes.data || []);

      // Navigate
      if (nextLesson) {
        setTimeout(() => navigate(`/middle-school/lessons/${nextLesson.id}`), 800);
      } else {
        setTimeout(() => navigate("/middle-school"), 800);
      }
    } catch (e) {
      console.error("Submit failed:", e);
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const value = responses[field.id] || "";
    const common = {
      id: `field-${field.id}`,
      "data-testid": `ms-field-${field.id}`,
    };
    if (field.type === "text") {
      return (
        <input
          type="text"
          {...common}
          value={value}
          onChange={(e) => handleFieldChange(field.id, e.target.value)}
          placeholder={field.placeholder || ""}
          className="w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100"
        />
      );
    }
    if (field.type === "textarea") {
      return (
        <textarea
          {...common}
          value={value}
          onChange={(e) => handleFieldChange(field.id, e.target.value)}
          placeholder={field.placeholder || ""}
          rows={4}
          className="w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100"
        />
      );
    }
    if (field.type === "poll") {
      return (
        <div className="flex flex-wrap gap-2" data-testid={`ms-poll-${field.id}`}>
          {(field.options || []).map((opt) => {
            const selected = value === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => handleFieldChange(field.id, opt)}
                className={`px-4 py-2 rounded-full border-2 font-medium transition-all ${
                  selected
                    ? "bg-purple-500 border-purple-500 text-white shadow-md"
                    : "bg-white border-purple-200 text-purple-700 hover:border-purple-400"
                }`}
                data-testid={`ms-poll-option-${field.id}-${opt.replace(/\s+/g, "-")}`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      );
    }
    if (field.type === "submit") {
      return null; // handled by main submit button
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-pink-50 via-yellow-50 to-blue-50">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!currentLesson) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-yellow-50 to-blue-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="container mx-auto px-4 py-12 text-center">
          <p className="text-gray-700">Lesson not found.</p>
          <button
            onClick={() => navigate("/middle-school")}
            className="lemon-button mt-4 px-6 py-2 rounded-lg text-white font-semibold"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const fields = currentLesson.interactive_fields || [];
  const hasSubmitField = fields.some((f) => f.type === "submit");
  const submitLabel = fields.find((f) => f.type === "submit")?.label || "Continue";

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-yellow-50 to-blue-50">
      <Navbar user={user} onLogout={onLogout} />

      <div className="container mx-auto px-4 py-8 max-w-4xl" data-testid="ms-lesson-view">
        {/* Breadcrumb / Back */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <button
            onClick={() => navigate("/middle-school")}
            className="flex items-center gap-2 text-purple-600 font-semibold hover:underline"
            data-testid="ms-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </button>
          <div className="text-sm font-semibold text-gray-600" data-testid="ms-lesson-position">
            Lesson {currentIndex + 1} of {lessons.length}
          </div>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 rounded-2xl shadow-lg p-6 mb-6 text-white">
          <div className="text-xs uppercase tracking-wide font-semibold opacity-90 mb-1">
            {module?.title}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold" data-testid="ms-lesson-title">
            {currentLesson.title}
          </h1>
        </div>

        {/* Educator-only Teacher Note (school-readiness context) */}
        {(user?.role === "educator" || user?.role === "admin") && sessionV2?.teacher_note && (
          <div
            className="bg-indigo-50 border-2 border-indigo-200 rounded-xl p-4 mb-6 text-sm"
            data-testid="ms-teacher-note"
          >
            <div className="flex items-start gap-3">
              <div className="text-indigo-600 text-lg leading-none mt-0.5">📋</div>
              <div className="flex-1 min-w-0">
                <div className="text-indigo-900 font-bold text-xs uppercase tracking-wide mb-1">
                  Teacher Note · Why this lesson matters
                </div>
                <p className="text-indigo-900 leading-relaxed">{sessionV2.teacher_note}</p>
                {(sessionV2.career_pathway_tags?.length > 0 ||
                  sessionV2.academic_subject_tags?.length > 0) && (
                  <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                    {(sessionV2.career_pathway_tags || []).map((p) => (
                      <span key={p} className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-semibold">
                        💼 {p.replace(/_/g, " ")}
                      </span>
                    ))}
                    {(sessionV2.academic_subject_tags || []).map((s) => (
                      <span key={s} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-semibold">
                        📚 {s}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Lesson content (hidden for v2 — SessionFlow handles "Learn" stage) */}
        {currentLesson.lesson_type !== "session_v2" && (
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-6">
            <div
              className="whitespace-pre-wrap text-gray-700 leading-relaxed text-base sm:text-lg"
              data-testid="ms-lesson-content"
            >
              {currentLesson.content}
            </div>
          </div>
        )}

        {/* Lesson 1: focused step-by-step flow (Spark quiz → Profile → Activity → Builder) */}
        {currentLesson.lesson_type === "lesson1_flow" || (currentLesson.order === 1 && module?.order === 1) ? (
          <div className="mb-6">
            <MiddleSchoolLesson1Flow
              savedResponses={responses}
              onChange={handleLesson1Change}
              onContextChange={(prompts) => setDynamicPrompts(prompts)}
              onReadyToSubmit={handleLesson1Submit}
              submitting={submitting}
              videoUrl={sessionV2?.learn?.video_url}
            />
            <div className="mt-2 text-sm text-gray-500 flex items-center gap-2 h-5">
              {saving ? (
                <span className="flex items-center gap-1">
                  <Save className="w-4 h-4 animate-pulse" /> Saving...
                </span>
              ) : savedStatus ? (
                <span className="text-green-600 font-semibold flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" /> {savedStatus}
                </span>
              ) : null}
            </div>
          </div>
        ) : currentLesson.lesson_type === "session_v2" && sessionV2 ? (
          <div className="mb-6">
            <SessionFlow
              session={{ title: currentLesson.title, ...sessionV2 }}
              savedResponses={responses}
              onChange={handleLesson1Change}
              onContextChange={(prompts) => setDynamicPrompts(prompts)}
              onReadyToSubmit={handleSessionV2Submit}
              submitting={submitting}
            />
            <div className="mt-2 text-sm text-gray-500 flex items-center gap-2 h-5">
              {saving ? (
                <span className="flex items-center gap-1">
                  <Save className="w-4 h-4 animate-pulse" /> Saving...
                </span>
              ) : savedStatus ? (
                <span className="text-green-600 font-semibold flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" /> {savedStatus}
                </span>
              ) : null}
            </div>
          </div>
        ) : (
          /* Lessons 2-5: original interactive_fields renderer */
          fields.filter((f) => f.type !== "submit").length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-6" data-testid="ms-activity">
              <h2 className="text-xl font-bold text-gray-800 mb-5 flex items-center gap-2">
                <span className="text-2xl">🎯</span> Your Turn
              </h2>
              <div className="space-y-5">
                {fields
                  .filter((f) => f.type !== "submit")
                  .map((field) => (
                    <div key={field.id}>
                      <label
                        htmlFor={`field-${field.id}`}
                        className="block font-semibold text-gray-800 mb-2"
                      >
                        {field.label}
                      </label>
                      {renderField(field)}
                    </div>
                  ))}
              </div>

              {/* Save status */}
              <div className="mt-4 text-sm text-gray-500 flex items-center gap-2 h-5">
                {saving ? (
                  <span className="flex items-center gap-1">
                    <Save className="w-4 h-4 animate-pulse" /> Saving...
                  </span>
                ) : savedStatus ? (
                  <span className="text-green-600 font-semibold flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" /> {savedStatus}
                  </span>
                ) : null}
              </div>
            </div>
          )
        )}

        {/* LemonBot */}
        <div className="mb-6">
          <LemonBot
            lessonId={currentLesson.id}
            suggestedPrompts={dynamicPrompts || module?.lemonbot_prompts || []}
          />
        </div>

        {/* Submit / Continue (hidden for Lesson 1 + session_v2 — they handle completion internally) */}
        {currentLesson.order !== 1 && currentLesson.lesson_type !== "session_v2" && currentLesson.lesson_type !== "lesson1_flow" && (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            {prevLesson ? (
              <button
                onClick={() => navigate(`/middle-school/lessons/${prevLesson.id}`)}
                className="flex items-center gap-2 px-5 py-3 bg-white border-2 border-purple-300 text-purple-700 font-semibold rounded-xl hover:bg-purple-50"
                data-testid="ms-prev-lesson-btn"
              >
                <ArrowLeft className="w-4 h-4" /> Previous
              </button>
            ) : <div />}

            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="ms-submit-btn"
            >
              {submitting
                ? "Saving..."
                : hasSubmitField
                ? submitLabel
                : nextLesson
                ? "Save & Continue"
                : "Finish Module 🎉"}
              {!submitting && <ArrowRight className="w-5 h-5" />}
            </button>
          </div>
        )}
      </div>
      <LemonBotFloater
        lessonId={currentLesson.id}
        moduleId={module?.id}
        lessonTitle={currentLesson.title}
        moduleTitle={module?.title}
        activity="middle_school_lesson"
      />
    </div>
  );
}

export default MiddleSchoolLessonView;
